import { useEffect, useMemo, useRef, useState } from 'react'
import { Activity, AudioLines, Bed, ChevronLeft, ChevronRight, Dumbbell, Frown, Meh, Smile } from 'lucide-react'
import Avatar from '../../Avatar/Avatar'
import SquareText from '../icons/SquareText'
import { formatDateTime, STATUS_LABELS } from '../../../pages/ERP/Pacientes/patientFormat'
import PatientShiftStack from '../../../pages/ERP/Pacientes/PatientShiftStack'
import './ClinicalSpotlight.css'

const PAGE_SIZE = 4

const STATUS_BADGE = {
  internado: 'patients-badge patients-badge--internado',
  estavel: 'patients-badge patients-badge--estavel',
  observacao: 'patients-badge patients-badge--observacao',
  ambulatorial: 'patients-badge patients-badge--ambulatorial',
  alta: 'patients-badge patients-badge--alta'
}

const FEED_KIND = {
  sinais: { label: 'Sinais vitais', className: 'is-sinais', icon: Activity },
  evolução: { label: 'Evoluções', className: 'is-evolucao', icon: SquareText },
  reabilitação: { label: 'Reabilitação', className: 'is-reabilitacao', icon: Dumbbell }
}

function initials(name) {
  return String(name || '?').split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
}

function byRecent(left, right) {
  const leftTime = left.recorded_at ? new Date(left.recorded_at).getTime() : 0
  const rightTime = right.recorded_at ? new Date(right.recorded_at).getTime() : 0
  return rightTime - leftTime || String(left.name || '').localeCompare(String(right.name || ''), 'pt')
}

function reading(value, suffix = '') {
  if (value == null || value === '') return '—'
  return `${value}${suffix}`
}

function PainFace({ value }) {
  const score = Number(value)
  const known = Number.isFinite(score)
  const Icon = !known || score <= 3 ? Smile : score <= 6 ? Meh : Frown
  const tone = !known ? 'is-none' : score <= 3 ? 'is-low' : score <= 6 ? 'is-mid' : 'is-high'
  return (
    <span className={`clinical-spotlight-pain ${tone}`} title={known ? `Dor ${score} de 10` : 'Dor não aferida'}>
      <Icon size={18} />
      <small>{known ? score : '—'}</small>
    </span>
  )
}

export function ClinicalPatientStage({
  patients = [],
  selectedId,
  onSelectPatient,
  onOpenChart
}) {
  const ordered = useMemo(() => [...patients].sort(byRecent), [patients])
  const pageCount = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE))
  const [page, setPage] = useState(0)

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1))
  }, [pageCount])

  return (
    <section className="clinical-spotlight-stage clinical-spotlight-stage--end" data-onboarding="dashboard-roster" aria-label="Pacientes em acompanhamento">
      {ordered.length > PAGE_SIZE && (
        <>
          <button
            type="button"
            className="clinical-spotlight-arrow is-prev"
            aria-label="Pacientes anteriores"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            className="clinical-spotlight-arrow is-next"
            aria-label="Próximos pacientes"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      {ordered.length === 0 ? (
        <p className="patients-muted">Nenhum paciente ativo para exibir.</p>
      ) : (
        <div className="clinical-spotlight-grid">
          {ordered.map((patient, index) => {
            const selected = Number(selectedId) === Number(patient.id)
            const onPage = index >= page * PAGE_SIZE && index < (page + 1) * PAGE_SIZE
            return (
              <article
                key={patient.id}
                className={`patients-card clinical-spotlight-card${selected ? ' is-selected' : ''}${onPage ? '' : ' is-off-page'}`}
              >
                <button
                  type="button"
                  className="clinical-spotlight-open"
                  onClick={() => onSelectPatient?.(patient.id)}
                >
                  <div className="patients-card-top">
                    <div className="patients-identity">
                      <div className="patients-avatar-wrap">
                        {patient.photo_url
                          ? <img src={patient.photo_url} alt="" className="patients-avatar" />
                          : <div className="patients-avatar">{initials(patient.name)}</div>}
                      </div>
                      <div>
                        <span className={STATUS_BADGE[patient.status] || STATUS_BADGE.internado}>
                          {STATUS_LABELS[patient.status] || 'Paciente'}
                        </span>
                        <h3 className="patients-name">{patient.name}</h3>
                        <p className="patients-muted clinical-spotlight-when">
                          {patient.recorded_at ? `Aferido ${formatDateTime(patient.recorded_at)}` : 'Sem aferição recente'}
                        </p>
                      </div>
                    </div>
                    <div className="clinical-spotlight-corner">
                      <PainFace value={patient.pain_scale} />
                      <PatientShiftStack people={patient.on_shift} />
                    </div>
                  </div>
                  <p className="clinical-spotlight-line">
                    <Bed size={13} /> {patient.bed || 'Sem leito'}
                    <span>PA {reading(patient.systolic)}/{reading(patient.diastolic)}</span>
                    <span>SpO2 {reading(patient.oxygen, '%')}</span>
                    <span>{reading(patient.temperature, '°C')}</span>
                  </p>
                  <div className="patients-vitals clinical-spotlight-vitals">
                    <div className="patients-vitals-grid">
                      <div><small>PA</small><strong>{reading(patient.systolic)}/{reading(patient.diastolic)}</strong></div>
                      <div><small>FC</small><strong>{reading(patient.heart_rate)}</strong></div>
                      <div><small>SpO2</small><strong>{reading(patient.oxygen, '%')}</strong></div>
                      <div><small>Temp</small><strong>{reading(patient.temperature, '°C')}</strong></div>
                      <div><small>Glicemia</small><strong>{reading(patient.blood_glucose)}</strong></div>
                      <div><small>FR</small><strong>{reading(patient.respiratory_rate)}</strong></div>
                    </div>
                  </div>
                </button>
                {onOpenChart && (
                  <button type="button" className="clinical-spotlight-link" onClick={() => onOpenChart(patient.id)}>
                    Abrir prontuário
                  </button>
                )}
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

function ClinicalSpotlight({
  activity = [],
  onOpenChart,
  chart,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  emptyLabel = 'Nenhum lançamento ainda.',
  feedOnly = false
}) {
  const feedRef = useRef(null)
  const loadingRef = useRef(false)
  loadingRef.current = loadingOlder

  useEffect(() => {
    const node = feedRef.current
    if (!node) return undefined
    let lastY = 0
    const ask = () => {
      if (!hasMore || loadingRef.current) return
      onLoadOlder?.()
    }
    const start = (event) => {
      lastY = event.touches?.[0]?.clientY || 0
    }
    const move = (event) => {
      const touch = event.touches?.[0]
      if (!touch) return
      const delta = lastY - touch.clientY
      lastY = touch.clientY
      const atTop = node.scrollTop <= 0
      const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1
      if (atBottom && delta > 0) ask()
      const staysInFeed = (delta > 0 && !atBottom) || (delta < 0 && !atTop)
      if (!staysInFeed) return
      event.stopPropagation()
    }
    const onScroll = () => {
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 48) ask()
    }
    node.addEventListener('touchstart', start, { passive: true })
    node.addEventListener('touchmove', move, { passive: true })
    node.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      node.removeEventListener('touchstart', start)
      node.removeEventListener('touchmove', move)
      node.removeEventListener('scroll', onScroll)
    }
  }, [activity.length, hasMore, onLoadOlder])

  const panel = (
      <section className="patients-panel clinical-spotlight-panel" data-onboarding="dashboard-feed">
        <div className="clinical-feed-title">
          <span className="clinical-feed-title-icon"><AudioLines size={18} /></span>
          <div>
            <h2>Feed de Auditoria 360°</h2>
            <p>Role para ver o que aconteceu antes</p>
          </div>
        </div>
        {activity.length === 0 ? (
          <p className="patients-muted">{emptyLabel}</p>
        ) : (
          <div className="clinical-feed" ref={feedRef}>
            {activity.map((item) => {
              const kind = FEED_KIND[item.kind] || FEED_KIND.sinais
              const Icon = kind.icon
              return (
                <article key={item.id} className={`clinical-feed-item ${kind.className}`}>
                  <Avatar name={item.professional || 'Equipe'} imageUrl={item.professional_photo} size="sm" />
                  <div className="clinical-feed-copy">
                    <div className="clinical-feed-head">
                      <strong>{item.professional || 'Equipe'}</strong>
                      <span className="clinical-feed-kind"><Icon size={12} /> {kind.label}</span>
                    </div>
                    <p>{item.details}</p>
                    <div className="clinical-feed-meta">
                      <button type="button" className="clinical-feed-patient" onClick={() => onOpenChart?.(item.patient_id)} disabled={!onOpenChart}>
                        {item.patient_photo
                          ? <img src={item.patient_photo} alt="" />
                          : <span>{initials(item.patient_name)}</span>}
                        <em>{item.patient_name}</em>
                      </button>
                      <time dateTime={item.at}>{formatDateTime(item.at)}</time>
                    </div>
                  </div>
                </article>
              )
            })}
            {hasMore && (
              <button type="button" className="clinical-feed-more" onClick={() => onLoadOlder?.()} disabled={loadingOlder}>
                {loadingOlder ? 'Buscando o que veio antes...' : 'Ver o que aconteceu antes'}
              </button>
            )}
          </div>
        )}
      </section>
  )

  if (feedOnly) return panel

  return (
    <section className="clinical-spotlight" aria-label="Frequência cardíaca e registros do dia">
      <div className="clinical-spotlight-chart">
        {chart}
      </div>
      <div className="clinical-spotlight-panel-slot">
        {panel}
      </div>
    </section>
  )
}

export default ClinicalSpotlight
